import {
  Theme,
  alpha,
  checkboxClasses,
  iconButtonClasses,
  listClasses,
  listItemIconClasses,
  menuItemClasses,
  paperClasses,
  tablePaginationClasses,
} from "@mui/material";
import { gridClasses } from "@mui/x-data-grid";
import type { DataGridProComponents } from "@mui/x-data-grid-pro/themeAugmentation";

import { GridNoResultsOverlay, GridNoRowsOverlay } from "@/components/GridEmptyOverlay";
import GridExportToolbar from "@/components/GridExportToolbar";
import GridPaginationJump from "@/components/GridPaginationJump";
import { gray } from "@/theme/themePrimitives";

export const dataGridCustomizations: DataGridProComponents<Theme> & DataGridProComponents<Theme> = {
  MuiDataGrid: {
    defaultProps: {
      columnHeaderHeight: 40,
      // Every admin table can be exported (CSV / XLSX) and jump to a page.
      slots: {
        toolbar: GridExportToolbar,
        pagination: GridPaginationJump,
        noRowsOverlay: GridNoRowsOverlay,
        noResultsOverlay: GridNoResultsOverlay,
      },
      // First load shows row placeholders; a reload keeps the rows visible under a thin bar.
      slotProps: { loadingOverlay: { variant: "linear-progress", noRowsVariant: "skeleton" } },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "--DataGrid-overlayHeight": "300px",
        overflow: "clip",
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.background.default,
        [`& .${gridClasses.columnHeader}`]: {
          backgroundColor: theme.palette.background.paper,
          paddingTop: 0,
          paddingBottom: 0,
        },
        [`& .${gridClasses.columnHeaderTitleContainer}`]: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        [`& .${gridClasses.footerContainer}`]: {
          backgroundColor: theme.palette.background.paper,
        },
        [`& .${checkboxClasses.root}`]: {
          padding: theme.spacing(0.5),
          "& > svg": {
            fontSize: "1rem",
          },
        },
        [`& .${tablePaginationClasses.root}`]: {
          marginRight: theme.spacing(1),
          "& .MuiIconButton-root": {
            maxHeight: 32,
            maxWidth: 32,
            "& > svg": {
              fontSize: "1rem",
            },
          },
        },
      }),
      cell: ({ theme }) => ({ borderTopColor: theme.palette.divider }),
      menu: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        backgroundImage: "none",
        [`& .${paperClasses.root}`]: {
          border: `1px solid ${theme.palette.divider}`,
        },

        [`& .${menuItemClasses.root}`]: {
          margin: "0 4px",
        },
        [`& .${listItemIconClasses.root}`]: {
          marginRight: 0,
        },
        [`& .${listClasses.root}`]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }),

      row: ({ theme }) => ({
        "&:last-of-type": { borderBottom: `1px solid ${theme.palette.divider}` },
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&.Mui-selected": {
          background: theme.palette.action.selected,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }),
      iconButtonContainer: ({ theme }) => ({
        [`& .${iconButtonClasses.root}`]: {
          border: "none",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
          "&:active": {
            backgroundColor: gray[200],
          },
          ...theme.applyStyles("dark", {
            color: gray[50],
            "&:hover": {
              backgroundColor: alpha(gray[100], 0.1),
            },
            "&:active": {
              backgroundColor: alpha(gray[100], 0.16),
            },
          }),
        },
      }),
      menuIconButton: ({ theme }) => ({
        border: "none",
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: gray[100],
        },
        "&:active": {
          backgroundColor: gray[200],
        },
        ...theme.applyStyles("dark", {
          color: gray[50],
          "&:hover": {
            backgroundColor: alpha(gray[100], 0.1),
          },
          "&:active": {
            backgroundColor: alpha(gray[100], 0.16),
          },
        }),
      }),
      filterForm: ({ theme }) => ({
        gap: theme.spacing(1),
        alignItems: "flex-end",
      }),
      columnsManagementHeader: ({ theme }) => ({
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
      }),
      columnHeaderTitleContainer: {
        flexGrow: 1,
        justifyContent: "space-between",
      },
      columnHeaderDraggableContainer: { paddingRight: 2 },
    },
  },
};
